import React, { useState, useEffect, useCallback } from 'react';
import { useRecordContext } from 'react-admin';
import { Funeral } from '../types';
import { FuneralShowHeader } from './FuneralShowHeader';
import { FuneralShowFeed } from './FuneralShowFeed';
import { useFuneralClients } from './hooks/useFuneralClients';
import CremationTracker from '../components/CremationTracker';
import { KewixTransportStrip } from '../answering-service/KewixTransportStrip';
import { DeceasedPrimaryStrip } from './DeceasedPrimaryStrip';

interface FuneralShowRedesignProps {
  onMenuClick?: () => void;
  headerActions?: React.ReactNode;
}

export function FuneralShowRedesign({ onMenuClick, headerActions }: FuneralShowRedesignProps) {
  const record = useRecordContext<Funeral>();

  const { coloredClients, clientByRecipientId, clientByContactDetail, clientColorMap } =
    useFuneralClients(record);

  // ── Filter state ──────────────────────────────────────────────────────────
  const [activeClientIds, setActiveClientIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (coloredClients.length > 0) {
      setActiveClientIds(new Set(coloredClients.map((c) => c.id)));
    }
  }, [coloredClients]);

  const handleToggle = useCallback((clientId: number) => {
    setActiveClientIds((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  }, []);

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [showSystemEvents, setShowSystemEvents] = useState(true);
  const handleToggleSystem = useCallback(() => {
    setShowSystemEvents((prev) => !prev);
  }, []);


  if (!record) return null;

  return (
    <div className="cleo-ui flex flex-col h-full bg-background font-sans overflow-x-hidden">
      <FuneralShowHeader
        coloredClients={coloredClients}
        activeClientIds={activeClientIds}
        onToggle={handleToggle}
        showSystemEvents={showSystemEvents}
        onToggleSystem={handleToggleSystem}
        onMenuClick={onMenuClick}
        headerActions={headerActions}
        onStatusClick={() => setStatusDialogOpen(true)}
      />
      {/* Only shows while Kewix holds a transport for this case (T-150). */}
      <KewixTransportStrip funeralId={Number(record.id)} />
      {/* Main contact is the deceased: family messages are skipped (T-210). */}
      <DeceasedPrimaryStrip record={record} />
      <CremationTracker
        dialogOnly
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
      />
      <div className="flex-1 flex flex-col min-h-0">
        <FuneralShowFeed
          coloredClients={coloredClients}
          activeClientIds={activeClientIds}
          clientByRecipientId={clientByRecipientId}
          clientByContactDetail={clientByContactDetail}
          clientColorMap={clientColorMap}
          showSystemEvents={showSystemEvents}
        />
      </div>
    </div>
  );
}
